import type { MovementType } from '../characters/types';
import type { ArenaDef, TileDef, TilesetDef } from './types';

/**
 * How high a flier rides above the ground. It clears water and low props but not the 3.5 m walls
 * a castle family is built from, which is what makes a gate tile worth placing. Exported so that
 * a per-character flight height can replace it later without touching the terrain rule.
 */
export const FLIGHT_HEIGHT = 2;

/** The largest arena the format allows in either direction (D-17). */
export const MAX_ARENA_SIZE = 250;

/** Outside the arena: a wall nothing crosses and nothing sees over. */
const VOID_TILE: TileDef = { id: 'void', walk: 'blocked', height: Infinity };

/**
 * An arena's rows decoded into one tile family per cell, with the queries the simulation needs:
 * what is underfoot, whether a movement class may cross it, and whether something stands in the
 * way of a sight line.
 *
 * Tile coordinates are the arena's own: `x` east, `y` south, `y = 0` the first row. The world
 * puts that grid on the ground plane, so **tile `y` is world `z`** and world `y` is up.
 */
export class TileMap {
  readonly width: number;
  readonly height: number;
  /** Family index per cell, row-major: `index(x, y) = y * width + x`. */
  readonly cells: Uint8Array;
  readonly families: readonly TileDef[];

  constructor(arena: ArenaDef, tileset: TilesetDef) {
    this.width = arena.width;
    this.height = arena.height;
    const byId = new Map(tileset.tiles.map((t) => [t.id, t]));
    // The legend is small and fixed per arena, so resolve it once into a dense family table.
    const families: TileDef[] = [];
    const slotOf = new Map<string, number>();
    for (const [char, familyId] of Object.entries(arena.legend)) {
      const tile = byId.get(familyId);
      if (!tile)
        throw new Error(
          `Arena '${arena.id}': legend '${char}' names tile '${familyId}', which tileset '${tileset.id}' does not have`,
        );
      slotOf.set(char, families.length);
      families.push(tile);
    }
    this.families = families;
    this.cells = new Uint8Array(this.width * this.height);
    for (let y = 0; y < this.height; y++) {
      const row = arena.rows[y];
      if (row === undefined || row.length !== this.width)
        throw new Error(
          `Arena '${arena.id}': row ${y} is ${row?.length ?? 0} characters, expected ${this.width}`,
        );
      for (let x = 0; x < this.width; x++) {
        const char = row[x]!;
        const slot = slotOf.get(char);
        if (slot === undefined)
          throw new Error(`Arena '${arena.id}': row ${y} uses '${char}', which the legend lacks`);
        this.cells[y * this.width + x] = slot;
      }
    }
  }

  index(x: number, y: number): number {
    return y * this.width + x;
  }

  inside(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < this.width && y < this.height;
  }

  /** The family at tile coordinates; outside the arena is a solid void wall. */
  tile(x: number, y: number): TileDef {
    if (!this.inside(x, y)) return VOID_TILE;
    return this.families[this.cells[this.index(x, y)]!] ?? VOID_TILE;
  }

  /** The family under a world position. Tile `y` is world `z`. */
  tileAt(worldX: number, worldZ: number): TileDef {
    return this.tile(Math.floor(worldX), Math.floor(worldZ));
  }

  /**
   * The terrain rule (D-17): a **ground** fighter crosses `ground` tiles. A **flier** also
   * crosses `liquid`, and any obstacle shorter than its flight height. `teleport` characters
   * walk — blinking is an ability, not a terrain permission, or nothing would stop them.
   */
  passable(tile: TileDef, movement: MovementType): boolean {
    if (tile.walk === 'ground') return true;
    if (movement !== 'fly') return false;
    return tile.walk === 'liquid' || tile.height < FLIGHT_HEIGHT;
  }

  passableAt(worldX: number, worldZ: number, movement: MovementType): boolean {
    return this.passable(this.tileAt(worldX, worldZ), movement);
  }

  /**
   * Whether anything at least `eyeHeight` tall stands between two world points, walking only the
   * tiles the segment actually crosses (Amanatides & Woo). The cells the two endpoints stand in
   * are skipped: a fighter is not blocked by the tile it is standing on.
   *
   * Taking the *shorter* of two eye heights as the threshold is what makes true scale tell: a
   * dragon sees over a wall that hides a goblin.
   */
  blocksSight(ax: number, az: number, bx: number, bz: number, eyeHeight: number): boolean {
    let x = Math.floor(ax);
    let y = Math.floor(az);
    const endX = Math.floor(bx);
    const endY = Math.floor(bz);
    if (x === endX && y === endY) return false;
    const dx = bx - ax;
    const dz = bz - az;
    const stepX = Math.sign(dx);
    const stepY = Math.sign(dz);
    const tDeltaX = dx === 0 ? Infinity : Math.abs(1 / dx);
    const tDeltaY = dz === 0 ? Infinity : Math.abs(1 / dz);
    let tMaxX = dx === 0 ? Infinity : (stepX > 0 ? x + 1 - ax : ax - x) / Math.abs(dx);
    let tMaxY = dz === 0 ? Infinity : (stepY > 0 ? y + 1 - az : az - y) / Math.abs(dz);
    // A segment crosses at most one cell per row and column it spans, plus one.
    const limit = this.width + this.height + 2;
    for (let i = 0; i < limit; i++) {
      // `tMax` is the distance along the segment to the next cell boundary, in segment lengths:
      // once the nearest is past 1 the far endpoint has been reached.
      if (Math.min(tMaxX, tMaxY) > 1) return false;
      if (tMaxX < tMaxY) {
        tMaxX += tDeltaX;
        x += stepX;
      } else {
        tMaxY += tDeltaY;
        y += stepY;
      }
      if (x === endX && y === endY) return false;
      if (this.tile(x, y).height >= eyeHeight) return true;
    }
    return false;
  }
}
